(ns manatee.annotation.handlers
  (:require [ajax.edn :refer [edn-request-format]]
            [clojure.spec.alpha :as s]
            [kee-frame.core :as kf]
            [manatee.admin.task-config.app-db :as task-config-db]
            [manatee.ajax :as ajax]
            [manatee.annotation.app-db :as annotation-db]
            [manatee.annotation.autosaving :as autosaving]
            [manatee.annotation.ids :as ids]
            [manatee.components.editors.app-db :as editors-db]
            [manatee.components.editors.spellcheck :as spellcheck]
            [manatee.components.modal.db :as modal-db]
            [manatee.components.modal.handlers :as modal]
            [manatee.components.notifications.handlers :as notifications]
            [manatee.domain.item :as item]
            [manatee.domain.task :as task]
            [manatee.domain.task.spec :as task-spec]
            [manatee.edit.app-db :as edit-db]
            [manatee.fx.local-storage :as local-storage]
            [manatee.keyboard.handlers :as keyboard]
            [manatee.models.differences :as differences]
            [manatee.models.edit :as edit-model]
            [manatee.models.item :as item-model]
            [manatee.models.segment :as segment-model]
            [manatee.qa.app-db :as qa-db]
            [manatee.routing.url :as url]
            [manatee.session.handlers :as session]
            [manatee.ui.subs :as ui-subs]
            [manatee.user.app-db :as user-db]
            [medley.core :as medley]
            [re-frame.core :as rf]
            [rothko.editor.actions :as rothko-actions]
            [taoensso.timbre :as log]))

(kf/reg-controller ::annotate
                   {:params (fn [{:keys [handler]}]
                              (= handler ::annotate))
                    :start (fn [_ active]
                             (when active
                               [::init-annotate active]))})

(rf/reg-event-fx
 ::set-current-item
 (fn set-current-item
   [_ [_ id]]
   {::local-storage/fetch-values-by-key-fragment [(autosaving/key-fragment id)
                                                  [::set-current-item* id]]}))

(rf/reg-event-fx
 ::set-current-item*
 (fn set-current-item*
   [{:keys [db]} [_ id autosaved-values]]
   (let [{:keys [id] :as item} (annotation-db/get-item db id)]
     {:db (-> db
              (annotation-db/set-current-item id)
              (annotation-db/set-autosaved-values autosaved-values))
      :dispatch [::init-edit
                 (-> db edit-db/get-edits (get id))
                 item]})))

(defn item-selection-menu?
  [db]
  (= (annotation-db/get-primary-item db) (annotation-db/current-item db)))

(rf/reg-event-fx
 ::focus-current-item
 (fn focus-current-item
   [{:keys [db]} _]
   (let [items (rest (annotation-db/get-items db))
         {:keys [deferred? item-type]} (annotation-db/get-current-item-map db)
         edits (edit-db/get-edits db)
         keys-set #(set (map key %))
         in-edits? #(some #{(:id %)} (keys-set edits))
         current-item-index
         (if (item-selection-menu? db)
           (->> items
                (keep-indexed (fn [i item]
                                (when (not (in-edits? item)) i)))
                (first))
           0)]
     {:db db
      :fx (cond-> [[:dispatch [::focus-input current-item-index]]]
            (and deferred? (annotation-db/part-like? db item-type))
            (conj [:dispatch [::notifications/add-alert ids/annotation-alert-banner
                              {:message "You previously deferred this item."
                               :type :info}
                              :auto-dismiss? true]]))})))

(kf/reg-event-fx
 ::next-score-field
 (fn next-score-field
   [_ _]
   #?(:cljs (some-> js/document
                    (.-activeElement)
                    (.-nextElementSibling)
                    (.focus)))
   {}))

(kf/reg-event-fx
 ::prev-score-field
 (fn prev-score-field
   [_ _]
   #?(:cljs (-> js/document
                (.-activeElement)
                (.-previousElementSibling)
                (.focus)))
   {}))

(rf/reg-event-fx
 ::select-item-after-load
 (fn select-item-after-load
   [{:keys [db]} _]
   (let [id (if (annotation-db/suppress-part-screen? db)
              (-> (annotation-db/items-by-type db :answer) (first) :id)
              (annotation-db/get-primary-item db))]
     {:dispatch [::set-current-item id]})))

(rf/reg-event-fx
 ::select-item-after-submit
 (fn select-item-after-submit
   [{:keys [db]} _]
   (let [[{part-id :id}] (annotation-db/items-by-type db :part)
         edit-status-map (->> (edit-db/get-edits db)
                              (medley/map-vals :status))
         status-by-answer (->> (annotation-db/items-by-type db :answer)
                               (map :id)
                               (map (juxt identity edit-status-map)))
         first-pending-answer-item-id (->> status-by-answer
                                           (filter (comp nil? second))
                                           (ffirst))]
     (if first-pending-answer-item-id
       {:dispatch [::set-current-item first-pending-answer-item-id]}
       (if (annotation-db/suppress-part-screen? db)
         {}
         {:dispatch [::set-current-item part-id]})))))

(rf/reg-event-fx
 ::set-edit
 (fn set-edit
   [{:keys [db]} [_ {:keys [item-id] :as edit}]]
   {:db (edit-db/set-edit db item-id edit)}))

(rf/reg-event-fx
 ::unset-annotation
 (fn unset-annotation
   [{:keys [db]} _]
   {:db (-> db
            annotation-db/unset-annotation
            edit-db/unset-edits)}))

(rf/reg-event-db
 ::set-loading?
 (fn set-loading?
   [db [_ loading?]]
   (annotation-db/set-loading? db loading?)))

(rf/reg-event-fx
 ::load-items
 (fn load-items
   [{:keys [db]} [_ {:keys [previous?]}]]
   {:http-xhrio {:method :get
                 :uri (url/path-for
                       [:api/items
                        :user-id (user-db/get-user-id db)]
                       (when previous? {:previous true}))
                 :on-success [::load-items-success]
                 :on-failure [::load-failure "work items" {:user-id (user-db/get-user-id db)}
                              [::set-loading? false]]
                 :response-format (ajax/edn-response-format)}}))

(rf/reg-event-fx
 ::load-items-success
 (fn load-items-success
   [{:keys [db]} [_ {:keys [primary-item items edits]} :as event-vec]]
   (if-not (seq (task-config-db/get-task-config-map db))
     {:dispatch-later {:ms 200 :dispatch event-vec}} ; If we haven't finished loading task configs yet, we will try again later.
     {:db (-> db
              (annotation-db/set-items items)
              (edit-db/set-edits-by-item-id edits)
              (annotation-db/set-primary-item primary-item))
      :dispatch-n [[::set-loading? false]
                   [::select-item-after-load]
                   [::focus-current-item]]})))

(rf/reg-event-fx
 ::load-failure
 (fn load-failure
   [_ [_ kind log-info fail-info then]]
   (log/error (str "Error loading " kind) (assoc log-info :info fail-info))
   {:fx (vector [:dispatch [::notifications/add-alert ids/annotation-alert-banner
                            {:message (str "Unable to load " kind ". Please refresh the browser to try again or contact support if necessary.")
                             :type    :error}
                            :auto-dismiss? false]]
                [:dispatch then])}))

(rf/reg-event-fx
 ::load-previous-items
 (fn load-previous-items
   [_ _]
   {:dispatch-n [[::unset-annotation]
                 [::set-loading? true]
                 [::load-items {:previous? true}]]}))

(kf/reg-chain
 ::load-task-configs
 (fn load-task-configs
   [{:keys [db]} _]
   {:http-xhrio {:method :get
                 :uri (kf/path-for [:api/task-configs])
                 :on-failure [::load-failure "task configs" {:task-name (:item-task (annotation-db/get-current-item-map db))}]
                 :response-format (ajax/edn-response-format)}})
 (fn load-task-configs-success
   [{:keys [db]} [task-configs]]
   {:db (task-config-db/set-task-configs db task-configs)}))

(rf/reg-event-fx
 ::submit-edit
 (fn submit-edit
   [_ [_ opts]]
   {:dispatch [::send-edit
               (assoc opts
                      :on-failure [[::notifications/add-alert ids/annotation-alert-banner
                                    {:message "Unable to send your edit to the server. Please try submitting again or contact support if necessary."
                                     :type :error}
                                    :auto-dismiss? true]])]}))

(defn generate-part-edit
  [db edit original-qa-edit]
  (let [item (if original-qa-edit
               (assoc (qa-db/get-item db)
                      :id (:part-item-id original-qa-edit)
                      :item-type :part)
               (annotation-db/get-item db (annotation-db/get-primary-item db)))]
    (-> item
        (edit-model/item->base-edit)
        (merge (select-keys edit [:status :deferred? :reason :other-reason])))))

(defn maybe-assoc-generated-part-edit
  [{:keys [task-config-id] :as edit} db original-qa-edit]
  (cond-> edit
    (annotation-db/suppress-part-screen? db task-config-id)
    (assoc :generated-part-edit (generate-part-edit db edit original-qa-edit))))

(kf/reg-event-fx
 ::send-edit
 ;; Don't invoke this directly - call ::submit-edit or ::reject-item
 (fn send-edit
   [{:keys [db]} [{:keys [edit original-qa-edit on-failure on-success-main on-success]}]]
   (let [{:keys [item-id]} edit
         editor-id (or (:user-id original-qa-edit)
                       (user-db/get-user-id db))]
     {:http-xhrio {:method :post
                   :uri (kf/path-for [:api/user-item-edits
                                      :item-id item-id
                                      :user-id editor-id])
                   :params (maybe-assoc-generated-part-edit edit db original-qa-edit)
                   :format (edn-request-format)
                   :response-format (ajax/edn-response-format)
                   :on-success (conj (or on-success-main [::send-edit-success])
                                     on-success)
                   :on-failure [::send-edit-failure on-failure]}})))

(defn edit->session-event
  [{:keys [status id] :as edit}]
  {:event-type (if (= status :rejected)
                 :reject
                 :submit)
   :data (assoc edit :edit-id id)})

(defn log-edit-events
  [& edits]
  [::session/log-events {:events (->> edits
                                      (keep identity)
                                      (mapv edit->session-event))}])

(kf/reg-event-fx
 ::send-edit-success
 (fn send-edit-success
   [{:keys [db]} [dispatch-n {{:keys [item-type]} :edit
                              :keys [edit generated-part-edit]}]]
   (cond-> {:db db
            :dispatch-n (conj (or dispatch-n [])
                              [::notifications/add-alert ids/annotation-alert-banner
                               {:message "Annotation saved successfully"
                                :type :info}
                               :auto-dismiss? true]
                              (log-edit-events edit generated-part-edit)
                              [::set-edit edit]
                              [::select-item-after-submit])}
     ;; A set of items is complete when an edit for the part item has been submitted.
     (annotation-db/part-like? db item-type)
     (update :dispatch-n concat [[::autosaving/clear-autosaved-part]
                                 [::unset-annotation]
                                 [::load-items]])

     (not (annotation-db/part-like? db item-type))
     (update :dispatch-n concat [[::set-loading? false]]))))

(defn on-failure-message
  "Update the alert message in an on-failure"
  [on-failure message]
  (update-in on-failure [0]
             (fn [v]
               (update-in v [2] (fn [w]
                                  (assoc w :message message))))))

(rf/reg-event-fx
 ::send-edit-failure
 (fn send-edit-failure
   [{:keys [db event]} [_ on-failure info]]
   (let [cause (-> (filter :response event)
                   first
                   :response
                   :cause)]
     (log/error "Error sending edit" {:info info
                                      :on-failure on-failure
                                      :user-id (user-db/get-user-id db)})
     (if (= :invalid-item cause)
       {:dispatch-n (-> (on-failure-message on-failure "This item is not valid. Please report the item to continue working")
                        (conj [::set-loading? false]))}
       {:dispatch-n (conj on-failure [::set-loading? false])}))))

(rf/reg-event-fx
 ::answer-audio-destroyed
 (fn answer-audio-destroyed
   [_ [_ item-id timestamp]]
   {:dispatch [::session/log-events {:events [{:event-type :answer-audio-destroyed
                                               :data {:item-id item-id
                                                      :time timestamp}}]}]}))

(rf/reg-event-fx
 ::answer-audio-ready
 (fn answer-audio-ready
   [_ [_ item-id duration]]
   {:dispatch [::session/log-events {:events [{:event-type :answer-audio-ready
                                               :data {:item-id item-id
                                                      :duration duration}}]}]}))

(rf/reg-event-fx
 ::answer-audio-ended
 (fn answer-audio-ended
   [_ [_ item-id timestamp]]
   {:dispatch [::session/log-events {:events [{:event-type :answer-audio-ended
                                               :data {:item-id item-id
                                                      :time timestamp}}]}]}))

(rf/reg-event-fx
 ::answer-audio-paused
 (fn answer-audio-paused
   [_ [_ item-id timestamp]]
   {:dispatch [::session/log-events {:events [{:event-type :answer-audio-paused
                                               :data {:item-id item-id
                                                      :time timestamp}}]}]}))

(rf/reg-event-fx
 ::start-answer-audio
 (fn start-answer-audio
   [_ [_ item-id timestamp]]
   {:dispatch [::session/log-events {:events [{:event-type :start-answer-audio
                                               :data {:item-id item-id
                                                      :time timestamp}}]}]}))

(defn- key-event->numerical-value
  "Given a key event containing the key code for an ASCII digit character,
   return the corresponding digit value."
  [{:keys [keyCode] :as _key-event}]
  (- keyCode 48))

(kf/reg-event-fx
 :annotation/add-score-keys
 (fn add-score-keys
   [_ [_ keys]]
   (let [number (-> keys
                    (last)
                    (key-event->numerical-value))]
     {:dispatch [::select-radio-button {:element "flow" :number number}]})))

(kf/reg-event-fx
 ::init-annotate
 (fn init-annotate
   [{:keys [db]} [active]]
   {:db db
    :dispatch-n (cond-> [[::keyboard/init-keyboard [:annotation]]]
                  (and active (user-db/get-user-id db))
                  (conj [::unset-annotation]
                        [::notifications/load-announcements]
                        [::load-items]))}))

(rf/reg-event-fx
 ::focus-input
 (fn focus-input
   [_ [_ _index]]
   #?(:cljs (some-> js/document
                    (.getElementsByClassName "tab-item")
                    (aget _index)
                    (.focus)))
   {}))

(rf/reg-event-fx
 ::select-radio-button
 (fn select-radio-button
   [_ [_ _button]]
   ; Within the context of the current active element, find the radio button with `number` either as its value or as
   ; the value of its `data-ordinal` attribute and if it exists, "click" it and dispatch an event to move focus to the
   ; next scoring field.
   #?(:cljs (let [number (:number _button)
                  active-element (.-activeElement js/document)]
              (when-let [radio (or (.querySelector active-element
                                                   (str "input[value=\"" number "\"]"))
                                   (.querySelector active-element
                                                   (str "input[data-ordinal=\"" number "\"]")))]
                (.click radio)
                {:dispatch [::next-score-field]})))))

(rf/reg-event-fx
 :annotation/play-pause-player
 (fn play-pause-player
   [_ _]
   #?(:cljs (-> js/document
                (.getElementById "audio-wave-player__play-button")
                (.click)))
   {}))

(rf/reg-event-fx
 :annotation/click-submit
 (fn click-submit
   [{:keys [db]} _]
   #?(:cljs (if (and (.-activeElement js/document)
                     (item-selection-menu? db))
              (-> js/document
                  (.-activeElement)
                  (.click))
              (-> js/document
                  (.getElementById "submit-annotation")
                  (.click))))
   {:db db}))

(rf/reg-event-fx
 :annotation/enter-pressed
 (fn enter-pressed
   [{:keys [db]} _]
   (let [{:keys [task-config-id]} (annotation-db/get-current-item-map db)
         task-config (task-config-db/get-task-config db task-config-id)]
     (when (task/scoring? task-config)
       {:dispatch [:annotation/click-submit]}))))

(rf/reg-event-db
 ::set-feedback-status
 (fn set-feedback-status
   [db [_ {:keys [item-id status]}]]
   (annotation-db/set-feedback-status db item-id status)))

(defn remove-difference-flags
  [db {:keys [task-config-id]} edit]
  (let [task-config (task-config-db/get-task-config db task-config-id)
        {:keys [field-name]} (task/non-scoring-field task-config)]
    (cond-> edit
      field-name (update field-name differences/remove-flags))))

(defn non-form-based-edit
  "Returns elements of an edit that cannot be based on a single Rothko form."
  [db {:keys [item-type task-config-id id] :as _item}]
  (let [answer? (= item-type :answer)
        task-config (task-config-db/get-task-config db task-config-id)
        segment-annotation-field (task/segment-annotation-field task-config)
        time-markers-field (task/time-markers-field task-config)
        notes (annotation-db/get-notes db)]
    (cond-> nil
      notes
      (assoc :notes notes)

      (and segment-annotation-field
           answer?)
      (assoc (:field-name segment-annotation-field)
             (segment-model/to-segment-sequence
              (annotation-db/segments db id)
              (annotation-db/all-segment-documents db id)))

      (and time-markers-field
           answer?)
      (assoc (:field-name time-markers-field)
             (segment-model/time-markers-to-sequence
              (annotation-db/time-markers db id))))))

(defn maybe-assoc-feedback-id
  [edit {:keys [feedback] :as _item}]
  (cond-> edit
    feedback (assoc :feedback-id (:id feedback))))

(defn maybe-assoc-script
  [edit db item task-config]
  (let [transcript-on-reject? (task/modifier task-config :transcript-on-reject?)
        script (:script (annotation-db/intermediate-edit db (:id item) nil))]
    (cond-> edit
      (and transcript-on-reject? script)
      (assoc :script script))))

(defn base-annotation-edit
  "Returns a base edit containing annotation. It can be used mainly to make a 'done' annotation edit or a report."
  [db {:keys [id qa-edit-id] :as item}]
  (merge (->> (annotation-db/intermediate-edit db id qa-edit-id)
              (remove-difference-flags db item))
         (non-form-based-edit db item)
         (edit-model/item->base-edit item)))

(defn done-annotation-edit
  "Makes an annotation edit with `:done` status."
  [db item]
  (-> (base-annotation-edit db item)
      (assoc :status :done)
      (maybe-assoc-feedback-id item)))

(defn rejection-edit
  "Makes an edit with `:rejected` status."
  [db item task-config form-values]
  (let [field-names (task/field-names-by-item-type task-config (:item-type item))
        base-edit (if (task/modifier task-config :reject-with-annotation?)
                    (base-annotation-edit db item)
                    (edit-model/item->base-edit item))]
    (-> (select-keys item field-names)
        (merge base-edit)
        (assoc :status :rejected)
        (maybe-assoc-feedback-id item)
        (merge (medley/remove-vals nil? form-values))
        (medley/assoc-some :notes (annotation-db/get-notes db))
        (medley/update-existing :reason #(if (keyword? %) (vector %) %)))))

(defn add-message
  [messages message]
  (conj messages [:li message]))

(defn validation-message
  "Turns Spec errors into a user message."
  [{::s/keys [problems]} edit task-config]
  (when problems
    ;log problems to console in case it is not a known problem that would be displayed to user
    (log/error (str "validation-message problems: " problems)))

  (let [predicates (map :pred problems)
        find-predicate (fn [predicate-matcher] (some predicate-matcher predicates))]

    (cond-> [:ul "An error was found in the edit."]
      (find-predicate #{'manatee.domain.edit/not-too-few-diarisation-block-markers?}) (add-message "Too few diarisation-block markers")
      (find-predicate #{'manatee.domain.edit/not-too-many-diarisation-block-markers?}) (add-message "Too many diarisation-block markers")
      (find-predicate #{'manatee.domain.edit/not-too-many-roll-plays-markers?}) (add-message "Too many role-play markers")
      (find-predicate #{'manatee.domain.edit/not-too-few-roll-plays-markers?}) (add-message "Too few role-play markers")
      (find-predicate #{'manatee.domain.edit/time-markers-nested-ok?}) (add-message "Diarisation-block and role-play markers are not in the correct order")
      (find-predicate #(contains? (-> % flatten set) 'manatee.domain.task.spec/run-custom-validation)) (add-message (task-spec/run-custom-validation edit task-config)))))

(rf/reg-event-fx
 ::validate
 (fn validate
   [{:keys [db]} [_ {:keys [item edit rejection? on-success]}]]
   (let [{:keys [id item-type task-config-id qa-edit-id]} item
         task-config (task-config-db/get-task-config db task-config-id)
         spec (delay (task-spec/task-config->edit-spec task-config edit))
         form-based-validation? (or (not rejection?)
                                    (task/modifier task-config :reject-with-annotation?))]
     (cond
       (and (task/form-based? task-config item-type)
            form-based-validation?
            (not (annotation-db/valid? db id qa-edit-id)))
       {:db (annotation-db/set-validate-now db id qa-edit-id true)}

       (and (some? edit)
            (not (s/valid? @spec edit)))
       {:dispatch [::notifications/add-alert ids/annotation-alert-banner
                   {:message (validation-message (s/explain-data @spec edit) edit task-config) :type :error}
                   :auto-dismiss? true]}

       :else
       {:dispatch on-success}))))

(rf/reg-event-fx
 ::submit
 (fn submit
   [{:keys [db]} [_ {:keys [item] :as opts}]]
   (let [edit (done-annotation-edit db item)]
     {:dispatch
      [::validate
       {:item item
        :edit edit
        :on-success [::submit* (assoc opts :edit edit)]}]})))

(rf/reg-event-fx
 ::submit*
 (fn submit*
   [_ [_ {:keys [original-qa-edit] {:keys [id feedback]} :item :as opts}]]
   {:fx (cond-> [[:dispatch [::modal/hide :submit-item-modal]]
                 [:dispatch [::set-loading? true]]
                 [:dispatch [::submit-edit opts]]]
          (and (not original-qa-edit) feedback)
          (conj [:dispatch [::set-feedback-status {:item-id id :status :done}]]))}))

(rf/reg-event-fx
 ::spellcheck-before-submit
 (fn spellcheck-before-submit
   [{:keys [db]} [_ {:keys [id] :as item} {:keys [field-name]}]]
   (let [words (->> (annotation-db/intermediate-edit db id nil) field-name spellcheck/script->words)
         spelling-dictionary (editors-db/spelling-dictionary db)
         misspelled-words (spellcheck/filter-misspelled-words spelling-dictionary words)]
     (if (seq misspelled-words)
       {:dispatch-n [[::spellcheck/set-misspelled-words misspelled-words]
                     [::modal/show :submit-item-modal]]}
       {:dispatch [::submit {:item item}]}))))

(rf/reg-event-fx
 ::submit-annotation
 (fn submit-annotation
   [_ [_ {:keys [item-type] :as item} task-config incomplete-feedback? unannotated-answers]]
   {:dispatch
    (cond
      (and (= item-type :part) incomplete-feedback?)
      [::notifications/add-alert ids/annotation-alert-banner
       {:message "Part cannot be submitted until all answer feedback is complete." :type :error}
       :auto-dismiss? true]

      (and (= item-type :part) unannotated-answers)
      [::notifications/add-alert ids/annotation-alert-banner
       {:message "You must annotate all answers" :type :error}
       :auto-dismiss? true]

      (and (= item-type :answer) (task/script-annotation-field task-config))
      [::validate
       {:item item
        :on-success [::spellcheck-before-submit item (task/script-annotation-field task-config)]}]

      :else
      [::submit {:item item}])}))

;; Specifically for the New Linguaskill Business project, replicate the answer edit submission handler chain. These are
;; triggered to ensure that the required (empty) answer edits have been submitted prior to the part edit being
;; submitted and are "quieter" versions of their standard counterparts.

(rf/reg-event-fx
 ::hybrid-part-answer-audio-ready
 (fn hybrid-part-answer-audio-ready
   [_ [_ item duration]]
   {:dispatch-n [[::answer-audio-ready (:id item) duration]
                 [::submit-hybrid-part-answer-annotation item]]}))

(rf/reg-event-fx
 ::submit-hybrid-part-answer-annotation
 (fn submit-hybrid-part-answer-annotation
   [_ [_ item]]
   {:dispatch [::submit-hybrid-part-answer {:item item}]}))

(rf/reg-event-fx
 ::submit-hybrid-part-answer
 (fn submit-hybrid-part-answer
   [{:keys [db]} [_ {:keys [item] :as opts}]]
   (let [edit (done-annotation-edit db item)]
     {:dispatch-n [[::set-edit edit]
                   [::submit-hybrid-part-answer* (assoc opts
                                                        :edit edit)]]})))

(rf/reg-event-fx
 ::submit-hybrid-part-answer*
 (fn submit-hybrid-part-answer*
   [_ [_ opts]]
   {:dispatch [::submit-hybrid-part-answer-edit opts]}))

(rf/reg-event-fx
 ::submit-hybrid-part-answer-edit
 (fn submit-hybrid-part-answer-edit
   [_ [_ {:keys [edit] :as opts}]]
   {:dispatch [::send-edit (assoc opts
                                  :on-success-main [::session/log-events {:events [(edit->session-event edit)]}]
                                  :on-failure [[::notifications/add-alert ids/annotation-alert-banner
                                                :auto-dismiss? true
                                                {:message "Unable to contact the server. Please try refreshing the page or contact support if necessary."
                                                 :type :error}]])]}))

;; -----

;; Expects an event [::add-metadata {:some-metadata-key {:other \"values\"}}]
;; to merge into the item-map
(rf/reg-event-db
 ::add-metadata
 (fn add-metadata
   [db [_ metadata]]
   (let [primary-item (annotation-db/get-primary-item db)]
     (annotation-db/update-item db primary-item merge metadata))))

;; Expects an event [::remove-metadata :some-metadata-key] to then remove from the item-map
(rf/reg-event-db
 ::remove-metadata
 (fn remove-metadata
   [db [_ metadata-key]]
   (let [primary-item (annotation-db/get-primary-item db)]
     (annotation-db/update-item db primary-item dissoc metadata-key))))

(rf/reg-event-fx
 ::show-reject-item-modal
 (fn show-reject-item-modal
   [_ [_ item]]
   {:dispatch [::validate
               {:item item
                :rejection? true
                :on-success [::modal/show ids/reject-item-modal]}]}))

(rf/reg-event-fx
 ::reject-item
 (fn reject-item
   [{:keys [db]} [_ {:keys [item task-config form-values unannotated-answers] :as opts}]]
   (if (and (= (:item-type item) :part) unannotated-answers)
     {:dispatch [::notifications/add-alert
                 ids/rejection-alert-banner
                 {:message "You must finish marking all answers before rejecting"
                  :type :error}]}
     (let [edit (-> (rejection-edit db item task-config form-values)
                    (maybe-assoc-script db item task-config))]
       {:dispatch [::validate {:item item
                               :edit edit
                               :rejection? true
                               :on-success [::send-item-rejection (assoc opts :edit edit)]}]}))))

(rf/reg-event-fx
 ::send-item-rejection
 (fn send-item-rejection
   [{:keys [db]} [_ {:keys [edit]
                     {:keys [id feedback]} :item}]]
   (let [{:keys [original-qa-edit on-success-main]} (modal-db/modal-props db)]
     {:dispatch-n [[::set-loading? true]
                   [::send-edit
                    {:edit edit
                     :original-qa-edit original-qa-edit
                     :on-success-main on-success-main
                     :on-failure [[::notifications/add-alert ids/rejection-alert-banner
                                   {:message "Unable to send your rejection to the server. Please try rejecting again or contact support if necessary."
                                    :type :error}
                                   :auto-dismiss? true]]
                     :on-success [[::modal/hide ids/reject-item-modal]]}]
                   (when (and (not original-qa-edit) feedback)
                     [::set-feedback-status {:item-id id :status :done}])]})))

(rf/reg-event-fx
 ::report
 (fn report
   [{:keys [db]} [_ item form-value]]
   (let [report (assoc form-value :reporter-id (user-db/get-user-id db))
         edit (base-annotation-edit db item)]
     {:dispatch-n [[::send-report {:report report :edit edit}]
                   [::modal/hide :report-item-modal]
                   [::session/log-events {:events [{:event-type :report-item
                                                    :data report}]}]]})))

(rf/reg-event-fx
 ::set-notes
 (fn set-notes
   [{:keys [db]} [_ _item {:keys [notes]}]]
   {:db (annotation-db/set-notes db notes)
    :dispatch [::modal/hide :notes-modal]}))

(rf/reg-event-fx
 ::defer-item
 (fn defer-item
   [{:keys [db]} [_ {:keys [user-id]}]]
   (let [item-id (annotation-db/get-primary-item db)]
     {:http-xhrio {:method :post
                   :uri (kf/path-for [:api/defer-item
                                      :item-id item-id
                                      :user-id user-id])
                   :format (edn-request-format)
                   :response-format (ajax/edn-response-format)
                   :on-success [::defer-item-success item-id]
                   :on-failure [::defer-item-failure item-id]}
      :dispatch [::session/log-events {:events [{:event-type :defer-item
                                                 :data {:item-id item-id
                                                        :user-id user-id}}]}]})))

(rf/reg-event-fx
 ::defer-item-success
 (fn defer-item-success
   [_ _]
   {:dispatch-n [[::notifications/add-alert ids/annotation-alert-banner
                  {:message (str "Item deferred. It will reappear after you have submitted " item/annotations-since-deferral " more annotated parts.")
                   :type :info}
                  :auto-dismiss? true]
                 [::unset-annotation]
                 [::set-loading? true]
                 [::load-items]]}))

(rf/reg-event-fx
 ::defer-item-failure
 (fn defer-item-failure
   [_ [_ item-id]]
   (log/error "Error deferring item" {:item-id item-id})
   {:dispatch [::notifications/add-alert ids/annotation-alert-banner
               {:message (str "Unable to defer item. You may have tried to defer this item more than " item/defer-limit " times.")
                :type :error}
               :auto-dismiss? false]}))

(rf/reg-event-fx
 ::ignore-item
 (fn ignore-item
   [{:keys [db]} [_ {:keys [user-id]}]]
   (let [item-id (annotation-db/get-primary-item db)]
     {:http-xhrio {:method :post
                   :uri (kf/path-for [:api/ignore-item
                                      :item-id item-id
                                      :user-id user-id])
                   :format (edn-request-format)
                   :response-format (ajax/edn-response-format)
                   :on-success [::ignore-item-success item-id]
                   :on-failure [::ignore-item-failure item-id]}
      :dispatch [::session/log-events {:events [{:event-type :defer-item
                                                 :data {:item-id item-id
                                                        :user-id user-id}}]}]})))

(rf/reg-event-fx
 ::ignore-item-success
 (fn ignore-item-success
   [_ _]
   {:dispatch-n [[::notifications/add-alert ids/annotation-alert-banner
                  {:message "Item ignored"
                   :type :info}
                  :auto-dismiss? true]
                 [::unset-annotation]
                 [::set-loading? true]
                 [::load-items]]}))

(rf/reg-event-fx
 ::ignore-item-failure
 (fn ignore-item-failure
   [_ [_ item-id]]
   (log/error "Error ignoring item" {:item-id item-id})
   {:dispatch [::notifications/add-alert ids/annotation-alert-banner
               {:message "Unable to ignore item"
                :type :error}
               :auto-dismiss? false]}))

(rf/reg-event-fx
 ::send-report
 (fn send-report
   [{:keys [db]} [_ {:keys [report] {:keys [item-id user-id] :as edit} :edit}]]
   (let [user-id (or user-id (user-db/get-user-id db))
         report (assoc report
                       :edit edit
                       :user-id user-id
                       :item-id item-id)
         {:keys [base-url]} @(rf/subscribe [::ui-subs/config])]
     {:http-xhrio {:method :post
                   :uri (kf/path-for [:api/user-item-reports
                                      :item-id item-id
                                      :user-id user-id])
                   :params {:report report :link (url/absolute-path-for
                                                  base-url
                                                  [:manatee.qa.handlers/user-item-reports
                                                   :user-id user-id
                                                   :item-id item-id])}
                   :format (edn-request-format)
                   :response-format (ajax/edn-response-format)
                   :on-success [::send-report-success report]
                   :on-failure [::send-report-failure]}})))

(rf/reg-event-fx
 ::send-report-success
 (fn send-report-success
   [_ _]
   {:dispatch-n [[::notifications/add-alert ids/annotation-alert-banner
                  {:message "Report saved successfully"
                   :type :info}
                  :auto-dismiss? true]
                 [::autosaving/clear-autosaved-part]
                 [::unset-annotation]
                 [::load-items]]}))

(rf/reg-event-fx
 ::send-report-failure
 (fn send-report-success
   [_ [_ report]]
   (log/error "Error saving iteration" {:report report})
   {:dispatch [::notifications/add-alert ids/annotation-alert-banner
               {:message "Unable to save edit report. Please try submitting again or contact support if necessary."
                :type :error}
               :auto-dismiss? false]}))

(rf/reg-event-db
 ::set-loading-text-visible
 (fn set-loading-text-visible
   [db [_ visible?]]
   (annotation-db/set-loading-text-visible db visible?)))

(rf/reg-event-fx
 ::set-loading-text-visible-timeout
 (fn set-loading-text-visible-timeout
   [_ [_ timeout visible?]]
   {:fx [[:dispatch-later {:ms timeout :dispatch [::set-loading-text-visible visible?]}]]}))

(rf/reg-event-fx
 ::segments-changed
 (fn segments-changed
   [{:keys [db]} [_ {:keys [item autosaving-disabled?] {:keys [id]} :item} event]]
   {:db
    (cond-> db
      (contains? event :new-selected-region-id)
      (-> (annotation-db/set-current-segment-id id (:new-selected-region-id event))
          (annotation-db/set-segment-id-to-select id nil))

      (contains? event :new-regions)
      (annotation-db/set-segments id (:new-regions event))

      (contains? (:new-regions event) (annotation-db/current-segment-id db id))
      (annotation-db/set-segment-document
       id
       (-> (:new-regions event)
           (get (annotation-db/current-segment-id db id))
           (segment-model/update-segment-document (annotation-db/segment-document db id) (:full-transcript item)))))
    :dispatch-n
    [(when (and (contains? event :new-regions)
                (not autosaving-disabled?))
       [::autosaving/autosave-segments item (annotation-db/segments db id) (:new-regions event)])]}))

(rf/reg-event-fx
 ::set-segment-document
 (fn set-segment-document
   [{:keys [db]} [_ {:keys [item autosaving-disabled?]} document]]
   {:db (let [old-document (annotation-db/segment-document db (:id item))]
          (annotation-db/set-segment-document
           db
           (:id item)
           (merge document (select-keys old-document [::segment-model/update-key]))))
    :dispatch-n [(when-not autosaving-disabled?
                   [::autosaving/autosave-segment-script item document])]}))

(rf/reg-event-fx
 ::split-segment
 (fn split-segment
   [{:keys [db]} [_ {:keys [item autosaving-disabled?]} document]]
   (let [{:keys [id task-config-id full-transcript]} item
         {:keys [speakers]} (-> (task-config-db/get-task-config db task-config-id) task/segment-annotation-field)
         all-segments (annotation-db/segments db id)
         all-documents (annotation-db/all-segment-documents db id)
         segment-id (annotation-db/current-segment-id db id)
         segment (get all-segments segment-id)
         split-result (segment-model/split-segment segment document speakers full-transcript)
         {::segment-model/keys [updated-segment updated-document new-segments new-documents]} split-result
         new-ids (repeatedly (comp str random-uuid))
         new-all-segments (merge all-segments {segment-id updated-segment} (zipmap new-ids new-segments))
         new-all-documents (merge all-documents {segment-id updated-document} (zipmap new-ids new-documents))]
     {:db
      (-> db
          (annotation-db/set-segments id new-all-segments)
          (annotation-db/set-all-segment-documents id new-all-documents)
          (annotation-db/set-reload-segments id true))
      :dispatch-n
      [(when-not autosaving-disabled?
         [::autosaving/autosave-segments item all-segments new-all-segments])]})))

(rf/reg-event-fx
 ::time-markers-changed
 (fn time-markers-changed
   [{:keys [db]} [_ {:keys [item autosaving-disabled?]} time-markers]]
   {:db (annotation-db/set-time-markers db (:id item) time-markers)
    :dispatch-n [(when-not autosaving-disabled?
                   [::autosaving/autosave-time-markers item time-markers])]}))

(rf/reg-event-db
 ::init-edit
 (fn init-edit
   [db [_ edit {:keys [id item-type task-config-id full-transcript] :as item}]]
   (let [task-config (task-config-db/get-task-config db task-config-id)

         {::segment-model/keys [segments documents]}
         (when-let [{:keys [field-name]}
                    (when (= item-type :answer)
                      (task/segment-annotation-field task-config))]
           (-> (item-model/appropriate-edit edit item field-name)
               (some-> (segment-model/from-segment-sequence full-transcript))
               (or (segment-model/from-transcript full-transcript))
               (segment-model/merge-new
                (autosaving/autosaved-segments db id)
                (autosaving/autosaved-scripts db id)
                full-transcript)))

         time-markers (when-let [{:keys [field-name]} (task/time-markers-field task-config)]
                        (or (autosaving/autosaved-time-markers db id)
                            (-> (item-model/appropriate-edit edit item field-name)
                                (or (get item field-name))
                                segment-model/time-markers-from-sequence)))

         notes (item-model/appropriate-edit edit item :notes)]
     (-> db
         (annotation-db/set-notes notes)
         (annotation-db/set-segments id segments)
         (annotation-db/set-all-segment-documents id documents)
         (annotation-db/set-current-segment-id id nil)
         (annotation-db/set-time-markers id time-markers)))))

(rf/reg-event-fx
 ::form-change
 (fn form-change
   [{:keys [db]} [_ {:keys [id task-config-id item-type qa-edit-id] :as _item} edit valid?]]
   (let [task-config (task-config-db/get-task-config db task-config-id)]
     (when (task/form-based? task-config item-type)
       {:db (-> db
                (annotation-db/set-intermediate-edit id qa-edit-id edit)
                (annotation-db/set-valid id qa-edit-id valid?))}))))

(rf/reg-event-db
 ::reset-validate-now
 (fn reset-validate-now
   [db [_ item-id qa-edit-id]]
   (annotation-db/set-validate-now db item-id qa-edit-id false)))

(rf/reg-event-db
 ::reset-reload-segments
 (fn reset-reload-segments
   [db [_ item-id]]
   (annotation-db/set-reload-segments db item-id false)))

(rf/reg-event-fx
 ::on-editor-mount
 (fn on-editor-mount
   [_ [_ {:keys [spellcheck-disabled?] :as opts} {:keys [script]}]]
   {:dispatch-n [(when-not spellcheck-disabled?
                   [::spellcheck/check-script script])
                 (when (autosaving/script-autosaving-enabled? opts)
                   [::autosaving/init-script-autosaving opts script])]}))

(rf/reg-event-fx
 ::on-editor-transform
 (fn on-editor-transform
   [_ [_ {:keys [spellcheck-disabled? segment-annotation? item] :as opts} {:keys [script selection] :as document}]]
   {:dispatch-n [(when-not spellcheck-disabled?
                   [::spellcheck/check-script-debounced script])
                 (when (autosaving/script-autosaving-enabled? opts)
                   [::autosaving/autosave-script opts script])
                 (when segment-annotation?
                   [::set-segment-document opts document])
                 [::session/maybe-ping-events {:events [{:event-type :edit-script
                                                         :data {:item-id (:id item)
                                                                :actions @rothko-actions/!action-log
                                                                :script script
                                                                :selection selection}}]}]]}))

(rf/reg-event-fx
 ::on-editor-change-view
 (fn on-editor-change-view
   [{:keys [db]} [_ {:keys [item segment-annotation?]} view]]
   {:db (cond-> db segment-annotation? (annotation-db/set-editor-view (:id item) view))
    :dispatch [::session/maybe-ping-events {:events [{:event-type :change-view
                                                      :data {:item-id (:id item)
                                                             :view view}}]}]}))
